import * as React from 'react';
import { useCallback } from 'react';

import { Tab, TabList, TabPanel } from '@rbilabs/components-library/build/components/tabs';
import { useIntl } from 'react-intl';

import { IRestaurantNode } from 'generated/rbi-graphql';
import { useStoreLocatorTabs } from 'pages/store-locator/hooks/use-store-locator-tabs';
import { OrderLocatorTab } from 'state/launchdarkly/variations';
import type { TLocalizationKey } from 'types/i18n';

import { useSearching } from '../hooks/use-searching';
import { StoreCardList } from '../store-card-list';

const intlIdMap: Record<OrderLocatorTab, TLocalizationKey> = {
  [OrderLocatorTab.NEARBY]: 'nearby',
  [OrderLocatorTab.RECENT]: 'recents',
  [OrderLocatorTab.FAVORITE]: 'favorite',
  [OrderLocatorTab.DELIVERY]: 'delivery',
};

interface IListViewProps {
  errorFavs: null | Error;
  errorNearby: null | Error;
  errorRecent: null | Error;
  searchingNearby: boolean;
  searchingRecent: boolean;
  searchingFavs: boolean;
  isCateringBirthday?: boolean;
  storesFavs?: IRestaurantNode[];
  storesNearby?: IRestaurantNode[];
  storesRecent?: IRestaurantNode[];
  handleSelectStore?: (restaurant: IRestaurantNode) => void;
}

export const ListView: React.FC<IListViewProps> = ({
  errorFavs,
  errorNearby,
  errorRecent,
  searchingNearby,
  searchingRecent,
  searchingFavs,
  isCateringBirthday,
  storesFavs = [],
  storesNearby = [],
  storesRecent = [],
  handleSelectStore,
}) => {
  const { isSearchingFavorites, isSearchingNearby, isSearchingRecent } = useSearching({
    searchingNearby,
    searchingRecent,
    searchingFavs,
  });
  const { formatMessage } = useIntl();

  const { currentTabHasError, currentTabIndex, handleTabChange, locatorTabs } = useStoreLocatorTabs(
    { errorFavs, errorNearby, errorRecent }
  );

  const storesMap: {
    [K in OrderLocatorTab]: IRestaurantNode[];
  } = {
    [OrderLocatorTab.NEARBY]: storesNearby,
    [OrderLocatorTab.RECENT]: storesRecent,
    [OrderLocatorTab.FAVORITE]: storesFavs,
    [OrderLocatorTab.DELIVERY]: [],
  };

  const searchingMap: {
    [K in OrderLocatorTab]: boolean;
  } = {
    [OrderLocatorTab.NEARBY]: isSearchingNearby,
    [OrderLocatorTab.RECENT]: isSearchingRecent,
    [OrderLocatorTab.FAVORITE]: isSearchingFavorites,
    [OrderLocatorTab.DELIVERY]: false,
  };

  const tabs = locatorTabs.map(value => {
    return {
      label: formatMessage({ id: intlIdMap[value] }),
      content: (
        <StoreCardList
          currentTabHasError={currentTabHasError}
          restaurants={storesMap[value]}
          isSearching={searchingMap[value]}
          tab={value}
          handleSelectStore={handleSelectStore}
          isCateringBirthday={isCateringBirthday}
        />
      ),
    };
  });

  const onTabChange = useCallback(
    (event: React.ChangeEvent, value: number) => {
      event.preventDefault();
      handleTabChange(value);
    },
    [handleTabChange]
  );

  return (
    <>
      <TabList selectedIndex={currentTabIndex} onChange={onTabChange}>
        {tabs.map((tab, index) => (
          <Tab
            aria-controls={`tabpanel-${index}`}
            id={`tab-${index}`}
            key={index}
            label={tab.label}
            selected={currentTabIndex === index}
          />
        ))}
      </TabList>

      {tabs.map(({ content }, index) => (
        <TabPanel
          aria-labelledby={`tab-${index}`}
          id={`tabpanel-${index}`}
          key={index}
          selected={currentTabIndex === index}
        >
          {content}
        </TabPanel>
      ))}
    </>
  );
};
